import { FileText, LogIn, LogOut, ShieldCheck, UserRound } from "lucide-react";
import { useId, type FormEvent } from "react";

export type HostAccessPanelCopy = {
  title: string;
  description: string;
  emailLabel: string;
  passwordLabel: string;
  loginAction: string;
  loggingInAction: string;
  signedInLabel: string;
  logoutAction: string;
  loggingOutAction: string;
  meetingTitleLabel: string;
  meetingTitlePlaceholder: string;
  consentLabel: string;
  consentHelp: string;
};

type HostAccessPanelBaseProps = {
  copy: HostAccessPanelCopy;
  errorMessage?: string | null;
};

type SignedOutHostAccessPanelProps = HostAccessPanelBaseProps & {
  session: null;
  email: string;
  password: string;
  loginPending?: boolean;
  onEmailChange(value: string): void;
  onPasswordChange(value: string): void;
  onLogin(): void;
};

type SignedInHostAccessPanelProps = HostAccessPanelBaseProps & {
  session: {
    displayName: string;
    email?: string | null;
  };
  meetingTitle: string;
  consentConfirmed: boolean;
  controlsDisabled?: boolean;
  logoutPending?: boolean;
  onMeetingTitleChange(value: string): void;
  onConsentConfirmedChange(value: boolean): void;
  onLogout(): void;
};

export type HostAccessPanelProps =
  | SignedOutHostAccessPanelProps
  | SignedInHostAccessPanelProps;

export function HostAccessPanel(props: HostAccessPanelProps) {
  const titleId = useId();
  const errorId = useId();
  const emailId = useId();
  const passwordId = useId();
  const meetingTitleId = useId();
  const consentId = useId();
  const consentHelpId = useId();

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!props.session && !props.loginPending) props.onLogin();
  };

  return (
    <section className="host-access-panel" aria-labelledby={titleId}>
      <header className="host-access-panel__header">
        <span className="product-mark__icon" aria-hidden="true">
          <ShieldCheck size={20} strokeWidth={1.8} />
        </span>
        <div>
          <h2 id={titleId}>{props.copy.title}</h2>
          <p>{props.copy.description}</p>
        </div>
      </header>

      {props.errorMessage && (
        <p className="error-message" id={errorId} role="alert">
          {props.errorMessage}
        </p>
      )}

      {!props.session ? (
        <form
          className="host-access-panel__login"
          aria-describedby={props.errorMessage ? errorId : undefined}
          onSubmit={handleLogin}
        >
          <label htmlFor={emailId}>
            <UserRound size={16} aria-hidden="true" />
            <span>{props.copy.emailLabel}</span>
          </label>
          <input
            id={emailId}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            value={props.email}
            disabled={props.loginPending}
            required
            onChange={(event) => props.onEmailChange(event.currentTarget.value)}
          />

          <label htmlFor={passwordId}>
            <ShieldCheck size={16} aria-hidden="true" />
            <span>{props.copy.passwordLabel}</span>
          </label>
          <input
            id={passwordId}
            name="password"
            type="password"
            autoComplete="current-password"
            value={props.password}
            disabled={props.loginPending}
            required
            onChange={(event) => props.onPasswordChange(event.currentTarget.value)}
          />

          <button
            className="primary-button"
            type="submit"
            disabled={props.loginPending}
            aria-busy={props.loginPending}
          >
            <LogIn size={18} aria-hidden="true" />
            {props.loginPending ? props.copy.loggingInAction : props.copy.loginAction}
          </button>
        </form>
      ) : (
        <div className="host-access-panel__session">
          <div className="host-access-panel__identity">
            <UserRound size={18} aria-hidden="true" />
            <p>
              <span>{props.copy.signedInLabel}</span>{" "}
              <strong>{props.session.displayName}</strong>
              {props.session.email && <small>{props.session.email}</small>}
            </p>
            <button
              className="secondary-button"
              type="button"
              disabled={props.logoutPending || props.controlsDisabled}
              aria-busy={props.logoutPending}
              onClick={props.onLogout}
            >
              <LogOut size={16} aria-hidden="true" />
              {props.logoutPending ? props.copy.loggingOutAction : props.copy.logoutAction}
            </button>
          </div>

          <fieldset
            className="host-access-panel__meeting"
            disabled={props.controlsDisabled || props.logoutPending}
          >
            <legend className="sr-only">{props.copy.meetingTitleLabel}</legend>
            <label htmlFor={meetingTitleId}>
              <FileText size={16} aria-hidden="true" />
              <span>{props.copy.meetingTitleLabel}</span>
            </label>
            <input
              id={meetingTitleId}
              name="meeting-title"
              type="text"
              value={props.meetingTitle}
              placeholder={props.copy.meetingTitlePlaceholder}
              autoComplete="off"
              onChange={(event) => props.onMeetingTitleChange(event.currentTarget.value)}
            />

            <div className="host-access-panel__consent">
              <input
                id={consentId}
                name="recording-consent"
                type="checkbox"
                checked={props.consentConfirmed}
                aria-describedby={consentHelpId}
                onChange={(event) => props.onConsentConfirmedChange(event.currentTarget.checked)}
              />
              <div>
                <label htmlFor={consentId}>{props.copy.consentLabel}</label>
                <p id={consentHelpId}>{props.copy.consentHelp}</p>
              </div>
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
